import { useSupportSheet } from "../../components/SupportSheet";
import {
  formatBankAccountLabel,
  looksLikePayerCode,
} from "../../utils/bankAccountLabel";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  BankIcon,
  Button,
  CardIcon,
  CashSpecialIcon,
  Layout,
  SupportIcon,
  Title,
  WalletIcon,
} from "lola-framework-ui-test";
import { FlagCircle } from "../../components/FlagCircle";
import { QuoteInfo } from "../../components/QuoteInfo";
import { PayoutInfo } from "../../components/PayoutInfo";
import { Popup } from "../../components/Popup";
import { SelectPayoutMethodSlot, type FlatPayoutPick } from "../../components/request/SelectPayoutMethodSlot";
import { SelectDestinationCountrySlot } from "../../components/request/SelectDestinationCountrySlot";
import {
  PayoutDetailsSlot,
  type PayoutDetailsResult,
} from "../../components/request/PayoutDetailsSlot";
import { REQUEST_ROUTES, TITLE_COLOR } from "../../constants";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import { useSessionStore } from "../../store/useSessionStore";
import { useRequestStore } from "../../store/useRequestStore";
import {
  useComposeStore,
  type ComposeSelection,
} from "../../store/useComposeStore";
import {
  PayoutMethodType,
  type FundRequestPayoutMethod,
} from "../../interfaces/payout";
import {
  currenciesForCountry,
  expandCountryCurrencyOptions,
  pickDefaultDestinationCurrency,
} from "../../utils/catalogCountries";
import {
  resolveOriginForDestination,
  supportedDestinationCountries,
} from "../../utils/requestCorridors";
import {
  buildRequestPreQuote,
  mockCatalogFor,
  requestAvailablePayoutMethodsFor,
  requestDestinationCards,
  requestDestinationCountries,
  requestEstimateOptions,
  requestSavedBankAccounts,
} from "../../gallery/requestMock";
import type { IBankAccountPaymentMethod } from "../../interfaces/payment-method";
import { offerableSavedBankAccounts } from "../../utils/offerableBankAccounts";
import { payerCodesEquivalent } from "../../utils/payerCode";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


type PayoutStep = "method" | "details";

const defaultSelection = (): ComposeSelection => ({
  method: PayoutMethodType.BANK_ACCOUNT,
  option:
    requestEstimateOptions.find(
      (o) => o.payoutMethod === PayoutMethodType.BANK_ACCOUNT
    ) ?? null,
  payout: { bankId: "BBVA", accountNumber: "012345678901234568" },
});

/**
 * Compose-a-request screen. The requester enters how much they want to receive,
 * can change the destination country, then how they want to be paid out.
 *
 * Gallery adaptation: pre-quote is mocked via `buildRequestPreQuote`; confirming
 * stores the draft and routes straight to Minting (CIP gate omitted).
 * PUSH_TO_CARD pins a saved destination card from the first popup; add card
 * routes to AddCard pages (compose.selection survives).
 */
export const TransferPage = memo(() => {
  const { t } = useTranslation("request");
  const { theme, amountState } = useStore();
  const { session } = useSessionStore();
  const { setDraft } = useRequestStore();
  const navigate = useNavigate();
  const { openSupport, supportSheet } = useSupportSheet("request");
  const savedListsUi = useGalleryStore((s) => s.savedListsUi);

  const { amount } = amountState;
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } =
    colorConfig;

  const {
    destinationCountry: storedDestination,
    setDestinationCountry,
    destinationCurrency: storedCurrency,
    setDestinationCurrency,
    selection: storedSelection,
    setSelection,
  } = useComposeStore();

  const [selection, setLocalSelection] = useState<ComposeSelection | null>(
    () => storedSelection ?? defaultSelection()
  );

  useEffect(() => {
    if (storedSelection) setLocalSelection(storedSelection);
  }, [storedSelection]);

  const persistSelection = useCallback(
    (next: ComposeSelection | null) => {
      setLocalSelection(next);
      setSelection(next);
    },
    [setSelection]
  );

  const [showDestinationPopup, setShowDestinationPopup] = useState(false);
  const [showPayoutPopup, setShowPayoutPopup] = useState(false);
  const payoutPickerLoading = useGalleryPickerLoading(showPayoutPopup);
  const [payoutStep, setPayoutStep] = useState<PayoutStep>("method");
  const [pendingMethod, setPendingMethod] = useState<PayoutMethodType | null>(
    null
  );
  // Stand-in for the habits GET the live flow waits on, so the saved-list
  // shimmer is visible when the cash details step opens.
  const habitsLoadDelay = useGalleryPickerLoading(
    savedListsUi !== "empty" &&
      showPayoutPopup &&
      payoutStep === "details" &&
      pendingMethod === PayoutMethodType.CASH_PICKUP
  );
  const [shakePayout, setShakePayout] = useState(false);

  const config = session.data?.config;
  const corridors = config?.corridors ?? [];
  const initialCorridor = config?.defaultCorridor;

  const seededDestination =
    initialCorridor?.destinationCountryCode ??
    supportedDestinationCountries(corridors)[0] ??
    "MX";
  const destinationCountry = storedDestination ?? seededDestination;
  const pinnedDestination =
    initialCorridor?.destinationCountryCode ?? seededDestination;

  useEffect(() => {
    if (storedDestination == null) setDestinationCountry(seededDestination);
  }, [storedDestination, seededDestination, setDestinationCountry]);

  const sourceCountry =
    resolveOriginForDestination(corridors, destinationCountry) ??
    initialCorridor?.originCountryCode ??
    "US";

  const corridorDestinations = useMemo(() => {
    const fromCorridors = supportedDestinationCountries(corridors);
    if (fromCorridors.length > 0) return fromCorridors;
    return [seededDestination];
  }, [corridors, seededDestination]);

  const destinationCountriesForPicker = useMemo(
    () =>
      expandCountryCurrencyOptions(
        requestDestinationCountries.filter((c) =>
          corridorDestinations.includes(c.countryCode)
        )
      ),
    [corridorDestinations]
  );

  const showDestinationPicker = destinationCountriesForPicker.length > 0;

  const sourceCurrency = useMemo(() => {
    if (
      sourceCountry === config?.defaultCorridor?.originCountryCode &&
      config?.sourceCurrency
    ) {
      return config.sourceCurrency;
    }
    return config?.sourceCurrency ?? "USD";
  }, [
    sourceCountry,
    config?.defaultCorridor?.originCountryCode,
    config?.sourceCurrency,
  ]);

  const catalogDestination = useMemo(
    () =>
      requestDestinationCountries.find(
        (c) => c.countryCode === destinationCountry
      ),
    [destinationCountry]
  );

  useEffect(() => {
    if (!destinationCountry) return;
    const codes = currenciesForCountry(catalogDestination);
    if (codes.length === 0) return;
    const pinned =
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined;
    const preferred = pickDefaultDestinationCurrency(
      catalogDestination,
      pinned
    );
    if (!preferred) return;
    if (storedCurrency == null || !codes.includes(storedCurrency)) {
      setDestinationCurrency(preferred);
    }
  }, [
    destinationCountry,
    catalogDestination,
    storedCurrency,
    pinnedDestination,
    config?.destinationCurrency,
    setDestinationCurrency,
  ]);

  const destinationCurrency =
    storedCurrency ??
    pickDefaultDestinationCurrency(
      catalogDestination,
      destinationCountry === pinnedDestination
        ? config?.destinationCurrency
        : undefined
    ) ??
    "USD";

  const numericAmount = amount ? parseFloat(amount) : NaN;
  const hasAmount = !isNaN(numericAmount) && numericAmount > 0;

  const preQuote = useMemo(
    () =>
      hasAmount
        ? buildRequestPreQuote(sourceCountry, destinationCountry, numericAmount)
        : null,
    [hasAmount, sourceCountry, destinationCountry, numericAmount]
  );

  const availablePayoutMethods = useMemo(
    () =>
      hasAmount
        ? requestAvailablePayoutMethodsFor(
            sourceCountry,
            destinationCountry,
            numericAmount
          )
        : [],
    [hasAmount, sourceCountry, destinationCountry, numericAmount]
  );

  const routeUnavailable = Boolean(preQuote) && availablePayoutMethods.length === 0;

  const bankAvailable = availablePayoutMethods.includes(
    PayoutMethodType.BANK_ACCOUNT
  );
  const cardAvailable = availablePayoutMethods.includes(
    PayoutMethodType.PUSH_TO_CARD
  );
  const bankMethodOptions = useMemo(
    () =>
      (preQuote?.options ?? []).filter(
        (o) => o.payoutMethod === PayoutMethodType.BANK_ACCOUNT
      ),
    [preQuote?.options]
  );
  const catalogBankPayerCodes = useMemo(
    () => mockCatalogFor(destinationCountry).banks.map((b) => b.id),
    [destinationCountry]
  );
  const vaultAccounts = useMemo(
    () =>
      requestSavedBankAccounts.filter(
        (a) =>
          (a.countryCode || "").toUpperCase() ===
          destinationCountry.toUpperCase()
      ),
    [destinationCountry]
  );
  const offerableAccounts = useMemo(
    () =>
      offerableSavedBankAccounts(
        vaultAccounts,
        bankMethodOptions,
        catalogBankPayerCodes
      ),
    [vaultAccounts, bankMethodOptions, catalogBankPayerCodes]
  );
  const destinationCards = cardAvailable ? requestDestinationCards : [];
  const bankLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const bank of mockCatalogFor(destinationCountry).banks) {
      map[bank.id.toUpperCase()] = bank.name;
    }
    return map;
  }, [destinationCountry]);

  const handleDestinationChange = useCallback(
    (countryCode: string, currencyCode: string) => {
      if (
        countryCode !== destinationCountry ||
        currencyCode !== destinationCurrency
      ) {
        persistSelection(null);
        setPayoutStep("method");
        setPendingMethod(null);
        setShowPayoutPopup(false);
      }
      setDestinationCountry(countryCode);
      setDestinationCurrency(currencyCode);
    },
    [
      destinationCountry,
      destinationCurrency,
      setDestinationCountry,
      setDestinationCurrency,
      persistSelection,
    ]
  );

  const openMethodPicker = useCallback(() => {
    setPayoutStep("method");
    setPendingMethod(null);
    setShowPayoutPopup(true);
  }, []);

  const handleSelectMethod = useCallback((method: PayoutMethodType) => {
    setPendingMethod(method);
    setPayoutStep("details");
  }, []);

  const handleDetailsSubmit = useCallback(
    (result: PayoutDetailsResult) => {
      if (!pendingMethod) return;
      persistSelection({
        method: pendingMethod,
        option: result.option,
        payout: result.payout,
      });
      setShowPayoutPopup(false);
    },
    [pendingMethod, persistSelection]
  );

  const handleSelectCardInstrument = useCallback(
    (picked: {
      paymentMethodId: string;
      brand: string;
      last4: string;
      cardExpiry?: string;
      cardholderName?: string;
    }) => {
      const option =
        (preQuote?.options ?? requestEstimateOptions).find(
          (o) => o.payoutMethod === PayoutMethodType.PUSH_TO_CARD
        ) ?? null;
      persistSelection({
        method: PayoutMethodType.PUSH_TO_CARD,
        option,
        payout: {
          source: "saved",
          paymentMethodId: picked.paymentMethodId,
          brand: picked.brand,
          last4: picked.last4,
          cardExpiry: picked.cardExpiry,
          cardholderName: picked.cardholderName,
        },
      });
      setShowPayoutPopup(false);
      setPayoutStep("method");
      setPendingMethod(null);
    },
    [preQuote?.options, persistSelection]
  );

  const handleSelectBankInstrument = useCallback(
    (account: IBankAccountPaymentMethod) => {
      const bankCode = (account.bankName || "").toUpperCase();
      const option =
        bankMethodOptions.find((o) =>
          payerCodesEquivalent(o.payerCode, bankCode)
        ) ??
        bankMethodOptions[0] ??
        null;
      persistSelection({
        method: PayoutMethodType.BANK_ACCOUNT,
        option,
        payout: {
          source: "saved",
          paymentMethodId: account.paymentMethodId,
          bankId: bankCode,
          last4: account.maskedAccountNumber.replace(/\D/g, "").slice(-4),
        },
      });
      setShowPayoutPopup(false);
      setPayoutStep("method");
      setPendingMethod(null);
    },
    [bankMethodOptions, persistSelection]
  );

  const handlePayoutPick = useCallback(
    (pick: FlatPayoutPick) => {
      if (pick.kind === "card") {
        handleSelectCardInstrument(pick.card);
        return;
      }
      if (pick.kind === "bank") {
        handleSelectBankInstrument(pick.account);
        return;
      }
      if (pick.kind === "add") {
        setShowPayoutPopup(false);
        navigate(REQUEST_ROUTES.ADD_CARD);
        return;
      }
      if (pick.kind === "addBank") {
        handleSelectMethod(PayoutMethodType.BANK_ACCOUNT);
        return;
      }
      handleSelectMethod(pick.method);
    },
    [
      handleSelectCardInstrument,
      handleSelectBankInstrument,
      handleSelectMethod,
      navigate,
    ]
  );

  const payoutIcon = useMemo(() => {
    switch (selection?.method) {
      case PayoutMethodType.BANK_ACCOUNT:
        return <BankIcon size={20} />;
      case PayoutMethodType.CASH_PICKUP:
        return <CashSpecialIcon size={20} />;
      case PayoutMethodType.PUSH_TO_CARD:
        return <CardIcon size={20} />;
      default:
        return <WalletIcon size={20} />;
    }
  }, [selection]);

  const payoutSummary = useMemo(() => {
    if (!selection) return t("transfer.selectPayoutMethod");
    const method = t(`payout.methods.${selection.method}`);
    const { payout } = selection;
    switch (selection.method) {
      case PayoutMethodType.BANK_ACCOUNT: {
        const tail =
          payout.accountNumber?.replace(/\D/g, "").slice(-4) || payout.last4;
        const stored =
          payout.bankId && !looksLikePayerCode(payout.bankId)
            ? payout.bankId
            : null;
        const line = formatBankAccountLabel(stored, tail);
        if (line) return line;
        return tail ? `${method} · •••• ${tail}` : method;
      }
      case PayoutMethodType.CASH_PICKUP:
        return payout.payerId ? `${method} · ${payout.payerId}` : method;
      case PayoutMethodType.PUSH_TO_CARD:
        return payout.last4
          ? `${(payout.brand || "CARD").toUpperCase()}-${payout.last4}`
          : method;
      case PayoutMethodType.MOBILE_WALLET:
        return payout.phone ? `${method} · ${payout.phone}` : method;
      default:
        return method;
    }
  }, [selection, t]);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const payoutReady = Boolean(selection);
  const canConfirm = hasAmount && payoutReady && !routeUnavailable;
  const showRainbow = canConfirm || confirmLoading;
  const payoutPending = hasAmount && !payoutReady;

  const nudgeIncompleteCompose = useCallback(() => {
    setShakePayout(true);
    window.setTimeout(() => setShakePayout(false), 500);
    const scrollToGap = () => {
      const target =
        document.querySelector(".payout-container .payment-required") ??
        document.querySelector(".payout-container .shake") ??
        document.querySelector(".estimated-quote-section");
      target?.scrollIntoView({ behavior: "smooth", block: "center" });
    };
    requestAnimationFrame(scrollToGap);
    window.setTimeout(scrollToGap, 50);
  }, []);

  const handleConfirm = useCallback(() => {
    if (confirmLoading) return;

    // Send / deferred parity: CTA always pressable; nudge missing fields.
    if (!hasAmount || !payoutReady) {
      nudgeIncompleteCompose();
      return;
    }
    if (routeUnavailable) return;

    setConfirmLoading(true);
    setDraft({
      sourceCountry,
      destinationCountry,
      sourceCurrency,
      destinationCurrency,
      amount: numericAmount,
      payoutMethod: selection!.method as FundRequestPayoutMethod,
      payout: selection!.payout,
      ...(selection!.option?.capabilityKey
        ? { capabilityKey: selection!.option.capabilityKey }
        : {}),
    });

    setTimeout(() => {
      navigate(REQUEST_ROUTES.MINTING);
    }, 400);
  }, [
    selection,
    hasAmount,
    payoutReady,
    routeUnavailable,
    confirmLoading,
    nudgeIncompleteCompose,
    setDraft,
    sourceCountry,
    destinationCountry,
    sourceCurrency,
    destinationCurrency,
    numericAmount,
    navigate,
  ]);

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(-1)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="transfer-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <section>
            <Title title={t("transfer.title")} align="left" color={TITLE_COLOR} />
            <section
              className={`estimated-quote-section${
                shakePayout && !hasAmount ? " shake" : ""
              }`}
            >
              <section className="quote-container">
                <QuoteInfo
                  label={t("transfer.youRequest")}
                  currency={destinationCurrency}
                  textColor={bodyCopy}
                  mode="input"
                  isActive
                  hideCurrencySymbol={destinationCurrency !== "USD"}
                  icon={<FlagCircle iso2={destinationCountry.toLowerCase()} />}
                  showSelectorChevron={showDestinationPicker}
                  onSelect={
                    showDestinationPicker
                      ? () => setShowDestinationPopup(true)
                      : undefined
                  }
                />
              </section>
            </section>
            <section className="payout-container">
              <PayoutInfo
                title={t("transfer.receiveIn")}
                subtitle={payoutSummary}
                icon={payoutIcon}
                background={backgroundBtn}
                color={textColorBtn}
                locked={!hasAmount}
                onChange={openMethodPicker}
                className={`${payoutPending ? "payment-required" : ""} ${
                  shakePayout && !payoutReady ? "shake" : ""
                }`.trim()}
              />
              <BodyCopy
                className="footer-text transfer-timing-note"
                textColor={TITLE_COLOR}
              >
                <b>{t("transfer.shareableLink")}</b>
                {" "}
                <span
                  className="transfer-timing-note-rest"
                  style={{ color: bodyCopy }}
                >
                  {t("transfer.shareableLinkHint")}
                </span>
              </BodyCopy>
            </section>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <div className={`button-wrapper ${showRainbow ? "rainbow-ready" : ""}`}>
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={confirmLoading}
                onClick={handleConfirm}
              >
                {confirmLoading
                  ? t("transfer.prepare.loading")
                  : t("transfer.createRequest")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      <Popup
        visible={showDestinationPopup}
        onClose={() => setShowDestinationPopup(false)}
      >
        <SelectDestinationCountrySlot
          countries={destinationCountriesForPicker}
          selectedCountry={destinationCountry}
          selectedCurrency={destinationCurrency}
          onSelect={handleDestinationChange}
          onClose={() => setShowDestinationPopup(false)}
        />
      </Popup>

      <Popup visible={showPayoutPopup} onClose={() => setShowPayoutPopup(false)}>
        {payoutStep === "method" ? (
          <SelectPayoutMethodSlot
            selectedMethod={selection?.method ?? null}
            selectedPaymentMethodId={
              selection?.method === PayoutMethodType.PUSH_TO_CARD ||
              selection?.method === PayoutMethodType.BANK_ACCOUNT
                ? selection.payout.paymentMethodId ?? null
                : null
            }
            available={availablePayoutMethods}
            cards={destinationCards}
            accounts={bankAvailable ? offerableAccounts : []}
            loading={payoutPickerLoading}
            bankLabels={bankLabels}
            onPick={handlePayoutPick}
            onClose={() => setShowPayoutPopup(false)}
          />
        ) : (
          <PayoutDetailsSlot
            method={pendingMethod as PayoutMethodType}
            destinationCountry={destinationCountry}
            options={preQuote?.options ?? []}
            value={
              selection && selection.method === pendingMethod
                ? selection.payout
                : null
            }
            habitsBusy={savedListsUi === "loading" || habitsLoadDelay}
            habitsEmpty={savedListsUi === "empty"}
            onBack={() => setPayoutStep("method")}
            onClose={() => setShowPayoutPopup(false)}
            onSubmit={handleDetailsSubmit}
          />
        )}
      </Popup>
      {supportSheet}
    </>
  );
});
